import React, { useState } from 'react';
import { Trash2, Check } from 'lucide-react';
import { FormattedMoodboardItem } from '../../../hooks/queries/moodboardTypes';
import { ImageModal } from './ImageModal';
import { cn } from '../../../lib/utils';

interface ImageItemProps {
  image: FormattedMoodboardItem;
  onDelete: (imageId: string) => void;
  isSelected: boolean;
  onSelect: (imageId: string) => void;
}

export function ImageItem({ image, onDelete, isSelected, onSelect }: ImageItemProps) {
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  return (
    <>
      <div className="relative group aspect-square">
        <img
          src={image.url}
          alt=""
          className={cn(
            'absolute inset-0 w-full h-full object-cover rounded-lg cursor-pointer transition-all duration-200',
            isSelected && 'brightness-90 contrast-125' // Subtle image adjustment when selected
          )}
          loading="lazy"
          onClick={() => setShowModal(true)}
        />

        {/* Selection button - visible on hover or when selected */}
        <div
          className={cn(
            'absolute top-2 left-2 w-8 h-8 rounded-full transition-all duration-200',
            'flex items-center justify-center cursor-pointer',
            'shadow-sm',
            isSelected
              ? 'bg-blue-500 text-white scale-110 opacity-100'
              : 'bg-white/90 text-gray-400 hover:text-blue-500 opacity-0 group-hover:opacity-100'
          )}
          onClick={e => {
            e.stopPropagation();
            onSelect(image.id);
          }}
        >
          <Check
            size={18}
            className={cn(
              'transition-all duration-200',
              isSelected ? 'opacity-100 scale-100' : 'opacity-70 scale-90'
            )}
          />
        </div>

        {/* Delete button - only show when not in selection mode */}
        {!isSelected && (
          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={e => {
                e.stopPropagation();
                setShowDeleteConfirm(true);
              }}
              className="p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
              title="Delete image"
            >
              <Trash2 size={16} />
            </button>
          </div>
        )}

        {/* Delete confirmation */}
        {showDeleteConfirm && (
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center rounded-lg z-10"
            onClick={e => e.stopPropagation()}
          >
            <div className="bg-white rounded-lg p-4 shadow-lg max-w-[200px]">
              <p className="text-sm text-gray-900 mb-4">
                Are you sure you want to delete this image?
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    onDelete(image.id);
                    setShowDeleteConfirm(false);
                  }}
                  className="px-3 py-1.5 text-sm text-red-600 hover:text-red-700 font-medium"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Image modal */}
      {showModal && <ImageModal image={image} onClose={() => setShowModal(false)} />}
    </>
  );
}
